/**
 * ViewModeToggle — compact "Show details" switch.
 *
 * Reframes the journey view-mode control as a single boolean: the calm view is
 * the default, and the user opts INTO detail (metrics, progress %, ratings,
 * RAG) by flicking the switch on. Backed by the localStorage-persisted
 * `useJourneyViewMode` hook so the preference stays consistent across surfaces.
 *
 * Deliberately tiny and inline — it tucks into an existing header row (e.g.
 * beside a title or nav), never its own full-width row.
 *
 * Position with the optional `className` prop.
 */

import { cn } from '@/lib/utils'
import { Switch } from '@/components/ui/switch'
import { InfoTooltip } from '@/components/ui/InfoTooltip'
import { useJourneyViewMode } from '@/lib/journeyViewMode'

interface ViewModeToggleProps {
  /** Optional positioning / layout classes for the outer wrapper. */
  className?: string
}

export function ViewModeToggle({ className }: ViewModeToggleProps) {
  const [mode, setMode] = useJourneyViewMode()
  const detailed = mode === 'detailed'

  return (
    <label
      className={cn(
        'inline-flex items-center gap-1.5 cursor-pointer select-none whitespace-nowrap',
        className,
      )}
    >
      <Switch
        checked={detailed}
        onCheckedChange={(v) => setMode(v ? 'detailed' : 'simple')}
        aria-label="Show details"
        className="data-[state=checked]:bg-heritage-blue scale-90"
      />
      <span className="text-xs font-medium text-stone-500">Show details</span>
      <InfoTooltip text="Off keeps a calm, focused view. On reveals progress, ratings and metrics." />
    </label>
  )
}

export default ViewModeToggle
